import React, { useState } from 'react';
import axios from 'axios';
import { RefreshCw, PenLine } from 'lucide-react';
import { T, feldStil, knopfStil } from '../../lib/designTokens';
import { fehlerText, normalisiereDealId } from '../../lib/dokumentenAssistent';
import { Hinweis } from '../contracts/dokumentenAssistent/AuftragDetails';

// Netzzusage auswerten (seit 30.10.2026): Test aus der Admin-Uebersicht. "Trockenlauf"
// liest die Zusage und zeigt Werte, Belege und was geschrieben wuerde - schreibt nichts.
// "Jetzt auswerten und schreiben" schreibt, auch wenn die Datei schon ausgewertet ist. Beides
// laeuft auch abgeschaltet (Testknopf, AGENTS.md).

// Rendern und KI bis 200 s am Server, dazu Download und Antwort.
const ZEITGRENZE_MS = 290000;

const BEZEICHNUNG = {
  spannungsebene: 'Spannungsebene',
  art_uebergabe: 'Art der Übergabestation',
  name_trafostation: 'Name Trafostation',
  anschlussleistung_kw: 'Anschlussleistung (kW)',
  datum_zusage: 'Datum Netzzusage',
  leistungsbegrenzung: 'Leistungsbegrenzung bei Engpass',
  antragsnummer: 'Antragsnummer',
  reservierung_bis: 'Reservierung gültig bis',
  netzbetreiber: 'Netzbetreiber (nur Vergleich)',
  entfernung_m: 'Entfernung (m, nur JSON)',
  kosten: 'Kosten (nur JSON)',
  befristung: 'Befristung (nur JSON)',
};

const STATUS_TEXT = {
  trockenlauf: 'Trockenlauf – nichts geschrieben',
  geschrieben: 'Geschrieben',
  teilweise: 'Geschrieben, mit Hinweisen',
  nichts_erkannt: 'Nichts erkannt',
  pruefen: 'Von Hand prüfen',
  nichts_zu_tun: 'Keine Datei im Feld „Netzzusage“',
  laeuft_schon: 'Läuft gerade',
};

export default function NetzzusageTest({ apiBase, session, onFertig }) {
  const [dealEingabe, setDealEingabe] = useState('');
  const [laeuft, setLaeuft] = useState('');
  const [ergebnis, setErgebnis] = useState(null);
  const [fehler, setFehler] = useState('');

  const dealId = normalisiereDealId(dealEingabe);
  const bereit = Boolean(dealId) && !laeuft;

  const starte = async (op) => {
    if (!dealId) {
      setFehler('Bitte eine gültige Deal-ID eingeben – nur Ziffern.');
      return;
    }
    if (op === 'schreiben' && !window.confirm(`Netzzusage von Deal ${dealId} jetzt auswerten und die Felder in Bitrix schreiben?`)) return;
    setLaeuft(op);
    setErgebnis(null);
    setFehler('');
    try {
      const { data } = await axios.post(
        `${apiBase}/automation/netzzusage-auswertung`,
        { op, dealId },
        { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}, timeout: ZEITGRENZE_MS },
      );
      setErgebnis(data?.ergebnis || null);
      onFertig?.();
    } catch (error) {
      setFehler(fehlerText(error, op === 'test' ? 'Trockenlauf fehlgeschlagen.' : 'Auswertung fehlgeschlagen.'));
    } finally {
      setLaeuft('');
    }
  };

  const werte = ergebnis?.werte || {};
  const belege = ergebnis?.belege || {};
  const geschrieben = new Set(ergebnis?.geschrieben || []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
        <strong style={{ color: T.ink }}>Einrichten (einmal, in Bitrix, Admin):</strong> Entwicklerressourcen → Andere →
        Ausgehender Webhook, Ereignis <code>ONCRMDEALUPDATE</code>, Adresse{' '}
        <code>https://vertrag.en-power.eu/api/automation/netzzusage-auswertung</code>. Das Anwendungs-Token, das Bitrix
        dabei anzeigt, in Vercel als <code>BITRIX_NETZZUSAGE_TOKEN</code> hinterlegen – ohne bleibt der Weg zu.
        Ausgewertet wird nur eine neue Datei im Feld „Netzzusage“; die eigenen Schreibvorgänge lösen keinen zweiten Lauf aus.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={dealEingabe}
          onChange={(e) => setDealEingabe(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && bereit) starte('test'); }}
          placeholder="Deal-ID, z. B. 12016"
          aria-label="Deal-ID für die Netzzusage"
          inputMode="numeric"
          disabled={Boolean(laeuft)}
          style={{ ...feldStil, width: 200 }}
        />
        <button type="button" onClick={() => starte('test')} disabled={!bereit}
          style={{ ...knopfStil(true), opacity: bereit ? 1 : 0.6 }}>
          <RefreshCw size={13} style={laeuft === 'test' ? { animation: 'spin 1s linear infinite' } : undefined} />
          {laeuft === 'test' ? 'Liest …' : 'Trockenlauf'}
        </button>
        <button type="button" onClick={() => starte('schreiben')} disabled={!bereit}
          style={{ ...knopfStil(false), opacity: bereit ? 1 : 0.6 }}>
          <PenLine size={13} />
          {laeuft === 'schreiben' ? 'Schreibt …' : 'Jetzt auswerten und schreiben'}
        </button>
      </div>
      {dealEingabe.trim() && !dealId ? (
        <div style={{ fontSize: T.tMikro, color: T.gefahr, fontWeight: 600 }}>Eine Deal-ID besteht nur aus Ziffern.</div>
      ) : null}
      {fehler ? <Hinweis ton="gefahr">{fehler}</Hinweis> : null}

      {ergebnis ? (
        <div style={{ display: 'grid', gap: 10, borderTop: `1px solid ${T.hairline}`, paddingTop: 12 }}>
          <div style={{ fontSize: T.tMeta, color: T.ink, fontWeight: 700 }}>
            Deal {ergebnis.dealId}{ergebnis.dealTitel ? ` · ${ergebnis.dealTitel}` : ''} –{' '}
            {STATUS_TEXT[ergebnis.status] || ergebnis.status}
            {ergebnis.status === 'trockenlauf' && ergebnis.ergebnisStatus ? ` (wäre: ${STATUS_TEXT[ergebnis.ergebnisStatus] || ergebnis.ergebnisStatus})` : ''}
          </div>
          {ergebnis.dateiId ? (
            <div style={{ fontSize: T.tMikro, color: T.body }}>
              Datei {ergebnis.dateiId}{ergebnis.dateiName ? ` · ${ergebnis.dateiName}` : ''}{ergebnis.scan ? ' · Scan (Seitenbilder)' : ' · Text-PDF'}
            </div>
          ) : null}
          {Object.keys(werte).length ? (
            <table style={{ borderCollapse: 'collapse', fontSize: T.tMikro, color: T.body, width: '100%' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: T.ink }}>
                  <th style={{ padding: '4px 6px' }}>Wert</th>
                  <th style={{ padding: '4px 6px' }}>Erkannt</th>
                  <th style={{ padding: '4px 6px' }}>Beleg</th>
                  <th style={{ padding: '4px 6px' }}>{ergebnis.status === 'trockenlauf' ? 'Würde schreiben' : 'Geschrieben'}</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(BEZEICHNUNG).map((k) => (
                  <tr key={k} style={{ borderTop: `1px solid ${T.hairline}`, verticalAlign: 'top' }}>
                    <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{BEZEICHNUNG[k]}</td>
                    <td style={{ padding: '4px 6px', color: werte[k] == null ? T.body : T.ink, fontWeight: werte[k] == null ? 400 : 600 }}>
                      {werte[k] == null ? '–' : String(werte[k])}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      {belege[k] ? (
                        <span>
                          S. {belege[k].seite}: „{belege[k].zitat}“{' '}
                          <span style={{ color: belege[k].geprueft ? T.gut : T.warn, fontWeight: 600 }}>
                            {belege[k].geprueft ? 'geprüft' : 'aus Scan, bitte prüfen'}
                          </span>
                        </span>
                      ) : '–'}
                    </td>
                    <td style={{ padding: '4px 6px' }}>{geschrieben.has(k) ? 'ja' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {(ergebnis.hinweise || []).length ? (
            <Hinweis ton="warn">
              {(ergebnis.hinweise || []).map((h) => <div key={h}>{h}</div>)}
            </Hinweis>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
